import _orderBy from 'lodash/orderBy';

import appRouter from '@/js/helpers/app-router';

const operators = [
    {operator: '=', name: 'actions.operators.eq'},
    {operator: '!=', name: 'actions.operators.neq'},
    {operator: '>', name: 'actions.operators.gt'},
    {operator: '>=', name: 'actions.operators.gte'},
    {operator: '<', name: 'actions.operators.lt'},
    {operator: '<=', name: 'actions.operators.lte'},
];

const dateOperators = [
    {operator: '=', name: 'actions.date-operators.eq'},
    {operator: '>', name: 'actions.date-operators.gt'},
    {operator: 'multiple', name: 'actions.date-operators.multiple'},
];

const dates = [
    {date: 'received_at', name: 'actions.dates.received_at'},
    {date: 'accepted_at', name: 'actions.dates.accepted_at'},
    {date: 'changed_activity_at', name: 'actions.dates.changed_activity_at'},
];

export default class ActionUI {
    constructor(http) {
        this.activities = null;
        this.counters = null;
        this.criteria = null;
        this.dates = dates;
        this.effects = null;
        this.languages = null;
        this.roles = null;
        this.templates = null;
        this.currentTemplate = null;
        this.operators = operators;
        this.dateOperators = dateOperators;
        this.queues = null;
        this.states = null;
        this.users = null;
        this.messageUsers = null;
        this.workflows = null;
        this.tags = null;
        this.documentTags = null;
        this.publicationTags = null;

        this.http = http;
    }

    getActivities(payload) {
        if (Array.isArray(this.activities)) {
            return Promise.resolve(this.activities);
        }

        const url = appRouter('api.actions.ui.activities');
        const params = {ids: payload};

        return this.http.get(url, {params})
            .then((response) => {
                this.activities = response.data.data;

                return this.activities;
            });
    }

    getCounters() {
        if (Array.isArray(this.counters)) {
            return Promise.resolve(this.counters);
        }

        const url = appRouter('api.actions.ui.counters');

        return this.http.get(url)
            .then((response) => {
                this.counters = _orderBy(response.data.data, 'sort_index');

                return this.counters;
            });
    }

    getDates() {
        return Promise.resolve(this.dates);
    }

    getLanguages() {
        if (Array.isArray(this.languages)) {
            return Promise.resolve(this.languages);
        }

        const url = appRouter('api.actions.ui.languages');

        return this.http.get(url)
            .then((response) => {
                this.languages = response.data.data.sort((a, b) => a.sort_index - b.sort_index);

                return this.languages;
            });
    }

    getRoles() {
        if (Array.isArray(this.roles)) {
            return Promise.resolve(this.roles);
        }

        const url = appRouter('api.actions.ui.roles');

        return this.http.get(url)
            .then((response) => {
                this.roles = response.data.data
                    .map((role) => ({
                        id: role.name,
                        sort_index: role.sort_index,
                        name: role.description,
                    }))
                    .sort((a, b) => a.sort_index - b.sort_index);

                return this.roles;
            });
    }

    getTemplates() {
        if (Array.isArray(this.templates)) {
            return Promise.resolve(this.templates);
        }

        const url = appRouter('api.actions.ui.templates');

        return this.http.get(url)
            .then((response) => {
                this.templates = _orderBy(response.data.data, 'name');

                return this.templates;
            });
    }

    getTemplate(templateId) {
        if (this.currentTemplate && this.currentTemplate.id === templateId) {
            return Promise.resolve(this.currentTemplate);
        }

        const url = appRouter('api.actions.ui.template', {template: templateId});

        return this.http.get(url)
            .then((response) => {
                this.currentTemplate = response.data.data;

                return this.currentTemplate;
            });
    }

    getOperators() {
        return Promise.resolve(this.operators);
    }

    getDateOperators() {
        return Promise.resolve(this.dateOperators);
    }

    getQueues(payload) {
        if (Array.isArray(this.queues)) {
            return Promise.resolve(this.queues);
        }

        const url = appRouter('api.actions.ui.queues');
        const params = {ids: payload};

        return this.http.get(url, {params})
            .then((response) => {
                this.queues = _orderBy(response.data.data, 'sort_index');

                return this.queues;
            });
    }

    getStates() {
        if (Array.isArray(this.states)) {
            return Promise.resolve(this.states);
        }

        const url = appRouter('api.actions.ui.states');

        return this.http.get(url)
            .then((response) => {
                this.states = response.data.data;

                return this.states;
            });
    }

    getUsers(payload) {
        if (Array.isArray(this.users)) {
            return Promise.resolve(this.users);
        }

        payload = Array.isArray(payload)
            ? payload.filter((id) => /^\d+$/.test(id))
            : null;

        const url = appRouter('api.actions.ui.users');
        const params = {ids: payload};

        return this.http.get(url, {params})
            .then((response) => {
                this.users = _orderBy(response.data.data, (user) => String(user.nickname).toLocaleLowerCase());

                return this.users;
            });
    }

    getUsersForMessage(payload) {
        if (Array.isArray(this.messageUsers)) {
            return Promise.resolve(this.messageUsers);
        }

        payload = Array.isArray(payload)
            ? payload.filter((id) => /^\d+$/.test(id))
            : null;

        const url = appRouter('api.actions.ui.message-users');
        const params = {ids: payload};

        return this.http.get(url, {params})
            .then((response) => {
                this.messageUsers = _orderBy(
                    response.data.data,
                    (user) => String(user.nickname).toLocaleLowerCase(),
                );

                return this.messageUsers;
            });
    }

    getWorkflows() {
        if (Array.isArray(this.workflows)) {
            return Promise.resolve(this.workflows);
        }

        const url = appRouter('api.actions.ui.workflows');

        return this.http.get(url)
            .then((response) => {
                this.workflows = _orderBy(
                    response.data.data,
                    [
                        (workflow) => !!workflow.deleted_at,
                        (workflow) => workflow.name.toLocaleLowerCase(),
                    ],
                    ['asc', 'asc'],
                );

                return this.workflows;
            });
    }

    getTags() {
        if (Array.isArray(this.tags)) {
            return Promise.resolve(this.tags);
        }

        const url = appRouter('api.actions.ui.tags');

        return this.http.get(url)
            .then((response) => {
                this.tags = response.data.data;

                return this.tags;
            });
    }

    getDocumentTags() {
        if (Array.isArray(this.documentTags)) {
            return Promise.resolve(this.documentTags);
        }

        const url = appRouter('api.actions.ui.document-tags');

        return this.http.get(url)
            .then((response) => {
                this.documentTags = response.data.data;

                return this.documentTags;
            });
    }

    getPublicationTags() {
        if (Array.isArray(this.publicationTags)) {
            return Promise.resolve(this.publicationTags);
        }

        const url = appRouter('api.actions.ui.publication-tags');

        return this.http.get(url)
            .then((response) => {
                this.publicationTags = response.data.data;

                return this.publicationTags;
            });
    }
}
